import { Button } from '@mantine/core';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FiLock } from 'react-icons/fi';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { LoginPage } from '@web/features/auth/LoginPage';
import { AcceptInvitePage, ForgotPasswordPage, ResetPasswordPage } from '@web/features/auth/PasswordPages';
import { SetupPage } from '@web/features/auth/SetupPage';
import { HomePage } from '@web/features/home/HomePage';
import { ApiError } from '@web/lib/api';
import { landingPath, useAuth } from '@web/lib/auth';
import { setLineColors } from '@web/lib/lineColors';
import { useOrgContext } from '@web/lib/org';
import { setActiveUnits } from '@web/lib/units';
import { NAV_ITEMS } from './navigation';
import { MainShell } from './shell/MainShell';
import { PageBody } from './shell/PageHeader';
import { ComingSoonPage, EmptyState, FullPageLoader, NotFoundPage, RouteErrorPage } from './shell/StatePages';
import { StartRedirect } from './StartRedirect';

// Pages are split per route: the sign-in screen does not download charts, maps or the
// schematic canvas (doc 10 Phase 7).
const OrgSettingsPage = lazy(() => import('@web/features/admin/OrgSettingsPage').then((m) => ({ default: m.OrgSettingsPage })));
const OrgEquipmentTypesPage = lazy(() => import('@web/features/equipment/pages').then((m) => ({ default: m.OrgEquipmentTypesPage })));
const PlatformEquipmentPage = lazy(() => import('@web/features/equipment/pages').then((m) => ({ default: m.PlatformEquipmentPage })));
const PlatformHealthPage = lazy(() => import('@web/features/platform/HealthPage').then((m) => ({ default: m.PlatformHealthPage })));
const AssistantSettingsPage = lazy(() => import('@web/features/assistant/AssistantSettingsPage').then((m) => ({ default: m.AssistantSettingsPage })));
const ApiReferencePage = lazy(() => import('@web/features/platform/ApiReferencePage').then((m) => ({ default: m.ApiReferencePage })));
const AuditPage = lazy(() => import('@web/features/admin/AuditPage').then((m) => ({ default: m.AuditPage })));
const DataSourcesPage = lazy(() => import('@web/features/admin/DataSourcesPage').then((m) => ({ default: m.DataSourcesPage })));
const UnitsPage = lazy(() => import('@web/features/admin/UnitsPage').then((m) => ({ default: m.UnitsPage })));
const UsersPage = lazy(() => import('@web/features/admin/UsersPage').then((m) => ({ default: m.UsersPage })));
const CatalogPage = lazy(() => import('@web/features/catalog/CatalogPage').then((m) => ({ default: m.CatalogPage })));
const AlertsPage = lazy(() => import('@web/features/alerts/AlertsPage').then((m) => ({ default: m.AlertsPage })));
const ExplorePage = lazy(() => import('@web/features/explorer/ExplorePage').then((m) => ({ default: m.ExplorePage })));
const DashboardPage = lazy(() => import('@web/features/dashboards/DashboardsPage').then((m) => ({ default: m.DashboardPage })));
const DashboardsPage = lazy(() => import('@web/features/dashboards/DashboardsPage').then((m) => ({ default: m.DashboardsPage })));
const VizListPage = lazy(() => import('@web/features/dashboards/DashboardsPage').then((m) => ({ default: m.VizListPage })));
const LivePage = lazy(() => import('@web/features/dashboards/LivePage').then((m) => ({ default: m.LivePage })));
const SchematicPage = lazy(() => import('@web/features/schematics/SchematicScreen').then((m) => ({ default: m.SchematicPage })));
const PlatformAuditPage = lazy(() => import('@web/features/platform/PlatformPages').then((m) => ({ default: m.PlatformAuditPage })));
const PlatformOrgsPage = lazy(() => import('@web/features/platform/PlatformPages').then((m) => ({ default: m.PlatformOrgsPage })));
const PlatformUsersPage = lazy(() => import('@web/features/platform/PlatformPages').then((m) => ({ default: m.PlatformUsersPage })));
const PlatformTemplatesPage = lazy(() => import('@web/features/platform/TemplatesPage').then((m) => ({ default: m.PlatformTemplatesPage })));
const FieldDetail = lazy(() => import('@web/features/assets/AssetsPage').then((m) => ({ default: m.FieldDetail })));
const TestDetail = lazy(() => import('@web/features/assets/AssetsPage').then((m) => ({ default: m.TestDetail })));
const WellDetail = lazy(() => import('@web/features/assets/AssetsPage').then((m) => ({ default: m.WellDetail })));
const AssetsLayout = lazy(() => import('@web/features/assets/AssetsPage').then((m) => ({ default: m.AssetsLayout })));
const AssetsOverview = lazy(() => import('@web/features/assets/AssetsPage').then((m) => ({ default: m.AssetsOverview })));

/** Links from before wellsites were removed (/wellsites/...) open the same page under /assets. */
function LegacyWellsitesRedirect() {
  const { orgId, '*': rest = '' } = useParams();
  const location = useLocation();
  const target = /^(field|well|test)\/[^/]+$/.test(rest) ? `/${rest}` : '';
  return <Navigate to={`/o/${orgId}/assets${target}${location.search}`} replace />;
}
const ProfilePage = lazy(() => import('@web/features/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })));

/** Restores the session once, then renders the routes. */
function Root() {
  const { status, bootstrap } = useAuth();
  useEffect(() => {
    if (status === 'loading') void bootstrap();
    // Run once on app start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (status === 'loading') return <FullPageLoader />;
  return <Outlet />;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { status, me } = useAuth();
  const location = useLocation();
  if (status !== 'authenticated' || !me) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  // The account cannot do anything else until these are done (the API enforces it too).
  if (me.user.mustChangePassword || me.user.mfaSetupRequired) return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

function HomeRedirect() {
  const me = useAuth((s) => s.me);
  return <Navigate to={me ? landingPath(me) : '/login'} replace />;
}

/** Loads the org of the URL; an org the user cannot see renders the same "not found" page. */
function OrgBoundary() {
  const { orgId } = useParams();
  const org = useOrgContext(orgId);
  const { t } = useTranslation();
  if (org.isPending) return <FullPageLoader />;
  if (org.isError) {
    const forbidden = org.error instanceof ApiError && org.error.status === 403;
    if (forbidden) {
      return (
        <PageBody>
          <EmptyState icon={<FiLock size={22} />} title={t('shell.orgSuspendedTitle')} hint={org.error.message} />
        </PageBody>
      );
    }
    return <NotFoundPage />;
  }
  // Before any page of the organization renders: its units and its schematic line colours.
  setActiveUnits(org.data.customUnits, org.data.customUnitSystems);
  setLineColors(org.data.settings.lineColors);
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Outlet />
    </Suspense>
  );
}

/** Hides a page from users without the permission; the API refuses them as well. */
function RequirePermission({ permission, children }: { permission: string; children: ReactNode }) {
  const { orgId } = useParams();
  const org = useOrgContext(orgId);
  if (!org.data?.permissions.includes(permission)) return <NotFoundPage />;
  return <>{children}</>;
}

function RequirePlatform() {
  const me = useAuth((s) => s.me);
  if (!me?.user.platformRole) return <NotFoundPage />;
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Outlet />
    </Suspense>
  );
}

function NoAccessPage() {
  const { t } = useTranslation();
  const signOut = useAuth((s) => s.signOut);
  return (
    <PageBody>
      <EmptyState
        icon={<FiLock size={22} />}
        title={t('shell.noAccessTitle')}
        hint={t('shell.noAccessHint')}
        action={
          <Button variant="light" onClick={() => void signOut()}>
            {t('shell.signOut')}
          </Button>
        }
      />
    </PageBody>
  );
}

// Pages built in later phases keep their menu entry and show a placeholder until then.
const BUILT_ORG_PAGES = new Set(['home', 'assets', 'live', 'dashboards', 'schematics', 'alerts', 'explorer', 'adminUsers', 'adminDataSources', 'adminUnits', 'adminSettings', 'adminAudit']);
const placeholderOrgRoutes = NAV_ITEMS.filter((i) => i.scope === 'org' && !BUILT_ORG_PAGES.has(i.key)).map((item) => ({
  path: item.path,
  element: <ComingSoonPage titleKey={`nav.${item.key}`} />,
}));

export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/accept-invite', element: <AcceptInvitePage /> },
      { path: '/setup', element: <SetupPage /> },
      {
        path: '/',
        element: (
          <RequireAuth>
            <MainShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <HomeRedirect /> },
          {
            path: 'profile',
            element: (
              <Suspense fallback={<FullPageLoader />}>
                <ProfilePage />
              </Suspense>
            ),
          },
          { path: 'no-access', element: <NoAccessPage /> },
          {
            path: 'o/:orgId',
            element: <OrgBoundary />,
            children: [
              { index: true, element: <HomePage /> },
              { path: 'start', element: <StartRedirect /> },
              {
                path: 'assets',
                element: <AssetsLayout />,
                children: [
                  { index: true, element: <AssetsOverview /> },
                  { path: 'field/:id', element: <FieldDetail /> },
                  { path: 'well/:id', element: <WellDetail /> },
                  { path: 'test/:id', element: <TestDetail /> },
                ],
              },
              { path: 'wellsites/*', element: <LegacyWellsitesRedirect /> },
              {
                path: 'equipment-types',
                element: (
                  <RequirePermission permission="equipment.view">
                    <OrgEquipmentTypesPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'live',
                element: (
                  <RequirePermission permission="dashboards.view">
                    <LivePage />
                  </RequirePermission>
                ),
              },
              {
                path: 'dashboards',
                element: (
                  <RequirePermission permission="dashboards.view">
                    <DashboardsPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'dashboards/:id',
                element: (
                  <RequirePermission permission="dashboards.view">
                    <DashboardPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'schematics',
                element: (
                  <RequirePermission permission="schematics.view">
                    <VizListPage kind="schematics" />
                  </RequirePermission>
                ),
              },
              {
                path: 'schematics/:id',
                element: (
                  <RequirePermission permission="schematics.view">
                    <SchematicPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'alerts',
                element: (
                  <RequirePermission permission="alerts.view">
                    <AlertsPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'explorer',
                element: (
                  <RequirePermission permission="explorer.use">
                    <ExplorePage />
                  </RequirePermission>
                ),
              },
              {
                path: 'explorer/catalog',
                element: (
                  <RequirePermission permission="signals.manage">
                    <CatalogPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'admin/datasources',
                element: (
                  <RequirePermission permission="datasources.manage">
                    <DataSourcesPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'admin/units',
                element: (
                  <RequirePermission permission="signals.manage">
                    <UnitsPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'admin/users',
                element: (
                  <RequirePermission permission="org.users.manage">
                    <UsersPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'admin/settings',
                element: (
                  <RequirePermission permission="org.settings.manage">
                    <OrgSettingsPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'admin/audit',
                element: (
                  <RequirePermission permission="org.audit.view">
                    <AuditPage />
                  </RequirePermission>
                ),
              },
              ...placeholderOrgRoutes,
              { path: '*', element: <NotFoundPage /> },
            ],
          },
          {
            path: 'platform',
            element: <RequirePlatform />,
            children: [
              { index: true, element: <Navigate to="/platform/orgs" replace /> },
              { path: 'orgs', element: <PlatformOrgsPage /> },
              { path: 'users', element: <PlatformUsersPage /> },
              { path: 'audit', element: <PlatformAuditPage /> },
              { path: 'equipment', element: <PlatformEquipmentPage /> },
              { path: 'templates', element: <PlatformTemplatesPage /> },
              { path: 'assistant', element: <AssistantSettingsPage /> },
              { path: 'health', element: <PlatformHealthPage /> },
              { path: 'api', element: <ApiReferencePage /> },
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
